/**
 * Teams context bootstrap: wait for context (tenantId/UPN) and apply to store + localStorage.
 * Used by the app entry point before rendering.
 */
import { app } from "@microsoft/teams-js";
import { setTeamsContext } from "../redux/slices/login";

const CONTEXT_RETRY_DELAY_MS = 400;
const CONTEXT_MAX_ATTEMPTS = 15;

function hasValidContext(context) {
  const hasUserIdentity = !!(context?.user?.tenant?.id || context?.user?.userPrincipalName);
  const hasChannelScope = !!(
    context?.channel?.id &&
    (context?.channel?.ownerGroupId || context?.team?.groupId)
  );
  return hasUserIdentity && hasChannelScope;
}

/**
 * Retries app.getContext() until user identity and channel scope are present or max attempts reached.
 * @returns {Promise<object>} Teams context object
 */
export function waitForTeamsContext() {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    function tryGetContext() {
      app
        .getContext()
        .then((context) => {
          if (hasValidContext(context)) {
            resolve(context);
            return;
          }
          attempts += 1;
          if (attempts >= CONTEXT_MAX_ATTEMPTS) {
            resolve(context);
            return;
          }
          setTimeout(tryGetContext, CONTEXT_RETRY_DELAY_MS);
        })
        .catch((err) => {
          attempts += 1;
          if (attempts >= CONTEXT_MAX_ATTEMPTS) {
            reject(err);
            return;
          }
          setTimeout(tryGetContext, CONTEXT_RETRY_DELAY_MS);
        });
    }
    tryGetContext();
  });
}

/**
 * Normalize Teams SDK context to a plain serializable object for Redux.
 * SDK returns some values as class instances (e.g. app.appId as { idAsString }); convert to primitives.
 * @param {object} context - Raw context from app.getContext()
 * @returns {object} Plain object safe for Redux state
 */
function toSerializableTeamsContext(context) {
  if (context == null) return context;
  const out = {};
  for (const key of Object.keys(context)) {
    const v = context[key];
    if (key === "app" && v && typeof v === "object") {
      out.app = {};
      for (const appKey of Object.keys(v)) {
        const appVal = v[appKey];
        if (appKey === "appId" && appVal != null && typeof appVal === "object" && "idAsString" in appVal) {
          out.app.appId = appVal.idAsString;
        } else if (appVal !== undefined && typeof appVal !== "function") {
          out.app[appKey] = typeof appVal === "object" && appVal !== null && !(appVal instanceof Date)
            ? toSerializableTeamsContext(appVal)
            : appVal;
        }
      }
    } else if (v !== undefined && typeof v !== "function") {
      out[key] = typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date)
        ? toSerializableTeamsContext(v)
        : v;
    }
  }
  return out;
}

/**
 * Writes tenant/channel/group identifiers to localStorage and dispatches context to Redux.
 * clientId (Customer-Key) is set from env so get-customerconfig and other API calls have it in web and desktop.
 * Context is normalized to a serializable shape so Redux middleware does not warn.
 * @param {object} context - Teams context from app.getContext()
 * @param {object} store - Redux store (for setTeamsContext dispatch)
 */
export function applyTeamsContext(context, store) {
  const clientIdFromEnv = process.env.REACT_APP_CLIENT_ID;
  if (clientIdFromEnv) {
    localStorage.setItem("clientId", clientIdFromEnv);
  }
  if (context?.user) {
    if (context.user.tenant?.id) {
      localStorage.setItem("tenantId", context.user.tenant.id);
    }
    if (context.user.userPrincipalName) {
      localStorage.setItem("userPrincipalName", context.user.userPrincipalName);
    }
    if (context.user.id) {
      localStorage.setItem("userId", context.user.id);
    }
  }
  if (context?.team?.groupId) {
    localStorage.setItem("teamGroupId", context.team.groupId);
  }
  if (context?.channel?.id) {
    localStorage.setItem("channelId", context.channel.id);
  }
  if (context?.channel?.displayName) {
    localStorage.setItem("isGeneralChannel", context.channel.displayName);
  }
  if (context?.channel?.membershipType) {
    localStorage.setItem("channelMembershipType", context.channel.membershipType);
  }
  if (context?.channel?.ownerGroupId) {
    localStorage.setItem("ownerGroupId", context.channel.ownerGroupId);
  }
  
  if (context?.app?.host?.clientType) {
    localStorage.setItem("teamsHostClientType", context.app.host.clientType);
  }
  const effectiveGroupId =
    context?.channel?.membershipType === "Private"
      ? context?.channel?.ownerGroupId
      : context?.team?.groupId;
  if (effectiveGroupId) {
    localStorage.setItem("groupId", effectiveGroupId);
  }
  const serializable = toSerializableTeamsContext(context);
  store.dispatch(setTeamsContext(serializable));
}
